import { Card, Stack } from 'react-bootstrap';
import { Button, FormInput, FormWrapper, FormattedMessageDiv, GreenCheck, ExclamationCircle, IconAlert } from '@odmonorepo/core-components';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { unifiedApiClient } from 'merchant-api/unified';
import { useFormContext } from 'react-hook-form';

interface SetPasswordProps {
  className?: string;
  onSuccess?: () => void;
}

export const SetPassword = ({className, onSuccess}: SetPasswordProps) => {
  const { mutate, isPending, error, status } = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      unifiedApiClient.PUT('/client/user/v1/password', { body: { ...body, currentPasswordRequired: false } }),
    onSuccess,
  });
  return (
    <Card style={{ maxWidth: '600px' }} className={`p-5 w-100 ${className}`}>
      {error && <AlertMessage variant="danger" messageId="createAccount.error" />}
      {status === 'success' && <AlertMessage variant="success" messageId="createAccount.success" />}
      <FormWrapper
        schema={PasswordSchema}
        reValidateMode="onSubmit"
        onSubmit={data => mutate({ currentPassword: '', newPassword: data.account.pw })}
      >
        <Stack gap={5}>
          <FormattedMessageDiv className="h4 m-0" id="createAccount.title" />
          <FormInput name="account.pw" type="password" useYupErrors />
          <FormInput name="account.confirmPw" type="password" useYupErrors />
          <Requirements />
          <Button type="submit" loading={isPending} loadingText="Submitting..." className='mt-3'>Continue</Button>
        </Stack>
      </FormWrapper>
    </Card>
  );
};


const passwordRequirements = [
  { label: '8 - 30 characters', test: p => p?.length >= 8 && p?.length <= 30 },
  { label: 'At least one number (0 - 9)', test: p => /\d/.test(p) },
  { label: 'At least one special character', test: p => /[!#@$%^&*()_+~\-=`{}[\]:";'<>?,./]/.test(p) },
  { label: 'Upper and lowercase letters', test: p => /[A-Z]/.test(p) && /[a-z]/.test(p) },
];

const passwordValidation = yup.string().required('Password is required')
  .test('length', passwordRequirements[0].label, passwordRequirements[0].test)
  .test('number', passwordRequirements[1].label, passwordRequirements[1].test)
  .test('special', passwordRequirements[2].label, passwordRequirements[2].test)
  .test('case', passwordRequirements[3].label, passwordRequirements[3].test);

export const PasswordSchema = yup.object().shape({
  account: yup.object().shape({
    pw: passwordValidation,
    confirmPw: yup.string().required('Please confirm your password').oneOf([yup.ref('pw')], 'Passwords do not match'),
  }),
});

const Requirements = () => {
  const { watch, formState } = useFormContext();
  const password = watch('account.pw');
  const isSubmitted = formState.isSubmitted;

  return (
    <Stack gap={2} className='mt-1 fs-7'>
      <div className='fw-bold fs-6'>Password Requirements</div>
      {passwordRequirements.map((req, i) => {
        const passed = req.test(password);
        return (
          <Stack key={i} direction='horizontal' gap={2}>
            {passed ? <GreenCheck width='14px'/> : isSubmitted ? <ExclamationCircle /> : null}
            <div className={passed ? 'text-green' : isSubmitted ? 'text-red' : 'text-grey'}>
              {req.label}
            </div>
          </Stack>
        );
      })}
    </Stack>
  );
};

const AlertMessage = ({variant, messageId}) => (
  <IconAlert variant={variant}>
    <FormattedMessageDiv id={messageId} />
  </IconAlert>
);